import { useEffect, useState } from "react";
import { api } from "../lib/api";

// Fetches a sale's gallery photos once, in the order saved in the admin
// dashboard. Shared by the collection preview and the full gallery so both
// always show identical data. New admin uploads appear automatically.
export function useSaleGalleryPhotos(saleId) {
  const [photos, setPhotos] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/sale-gallery", { params: { sale_id: saleId } })
      .then(({ data }) => { if (!cancelled) setPhotos(data); })
      .catch(() => { if (!cancelled) setPhotos([]); });
    return () => { cancelled = true; };
  }, [saleId]);
  return photos;
}

// Complete photo gallery section — presentational; the page owns fetching and
// the shared lightbox. Renders nothing until the sale has photos.
export function SaleGallery({ saleId, photos, onOpenPhoto }) {
  if (!photos || photos.length === 0) return null;

  return (
    <section id="gallery" className="bg-ivory px-6 py-14 md:py-20" data-testid={`sale-photo-gallery-${saleId}`}>
      <div className="text-center max-w-2xl mx-auto">
        <p className="eyebrow-label">The collection</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-ink" data-testid={`sale-gallery-heading-${saleId}`}>
          Estate Sale Photo Gallery
        </h2>
        <p className="mt-3 text-sm sm:text-base text-ink/70 leading-relaxed">
          Browse photographs of items offered at the Easton estate sale. Select any photograph to view it enlarged.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 items-start max-w-6xl mx-auto" data-testid={`sale-gallery-grid-${saleId}`}>
        {photos.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onOpenPhoto(i)}
            className="block w-full border border-linen bg-white overflow-hidden group cursor-pointer"
            data-testid={`sale-gallery-thumb-${saleId}-${i}`}
            aria-label={`Enlarge photo ${i + 1}${p.caption ? `: ${p.caption}` : ""}`}
          >
            <img
              src={p.image_url}
              alt={p.caption || `Estate sale item photo ${i + 1}`}
              loading="lazy"
              className="w-full h-auto group-hover:opacity-90 transition-opacity"
            />
          </button>
        ))}
      </div>
    </section>
  );
}
